import { useState, useEffect } from 'react'
import { Folder, Plus, Save, X, ChevronDown, ChevronRight, Loader } from 'lucide-react'
import api from '../services/api'

interface ProjectsPageProps {
  onOpenProject: (projectName: string) => void
}

interface Category {
  id: string
  name: string
  icon: string
  color: string
  display_order: number
}

interface ProjectConfig {
  name?: string
  description?: string
  category_id?: string | null
  skills_enabled?: string[]
  context_file?: string
}

interface Project {
  name: string
  config: ProjectConfig
}


export default function ProjectsPage({ onOpenProject }: ProjectsPageProps) {
  const [categories, setCategories] = useState<Category[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  // Formulaire nouvelle catégorie
  const [showCategoryForm, setShowCategoryForm] = useState(false)
  const [categoryForm, setCategoryForm] = useState({ id: '', name: '', icon: '📁', color: '#6366f1' })
  const [categoryFormError, setCategoryFormError] = useState<string | null>(null)
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null)

  const slugify = (text: string) =>
    text.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')

  // Formulaire nouveau projet
  const [showProjectForm, setShowProjectForm] = useState(false)
  const [newProject, setNewProject] = useState({ name: '', description: '', category_id: 'VPS-HUB' })
  const [creating, setCreating] = useState(false)

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [catRes, projRes] = await Promise.all([
        api.get<Category[]>('/api/categories'),
        api.get<string[]>('/api/projects')
      ])
      const withConfigs = await Promise.all(
        projRes.data.map(async (name) => {
          try {
            const { data: config } = await api.get<ProjectConfig>(`/api/projects/${name}/config`)
            return { name, config }
          } catch {
            return { name, config: {} }
          }
        })
      )
      setCategories(catRes.data)
      setProjects(withConfigs)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur de chargement')
    } finally {
      setLoading(false)
    }
  }

  const toggleCollapse = (catId: string) => {
    const next = new Set(collapsed)
    next.has(catId) ? next.delete(catId) : next.add(catId)
    setCollapsed(next)
  }

  const handleCreateProject = async () => {
    if (!newProject.name) { alert('Nom requis'); return }
    if (!/^[a-z0-9_-]+$/.test(newProject.name)) {
      alert('Nom invalide : minuscules, chiffres, tirets uniquement')
      return
    }
    try {
      setCreating(true)
      await api.post('/api/projects', { name: newProject.name, description: newProject.description })
      if (newProject.category_id) {
        await api.put(`/api/projects/${newProject.name}/config`, {
          name: newProject.name,
          description: newProject.description,
          category_id: newProject.category_id
        })
      }
      setNewProject({ name: '', description: '', category_id: 'VPS-HUB' })
      setShowProjectForm(false)
      await fetchData()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erreur lors de la création')
    } finally {
      setCreating(false)
    }
  }

  const handleAddCategory = async () => {
    if (!categoryForm.name) { setCategoryFormError('Le nom est requis'); return }
    const id = categoryForm.id || slugify(categoryForm.name)
    if (!id) { setCategoryFormError('Nom invalide'); return }
    setCategoryFormError(null)
    try {
      await api.post('/api/categories', { ...categoryForm, id, display_order: categories.length + 1 })
      setShowCategoryForm(false)
      setCategoryForm({ id: '', name: '', icon: '📁', color: '#6366f1' })
      await fetchData()
    } catch (err: any) {
      setCategoryFormError(err.response?.data?.detail || 'Erreur lors de la création')
    }
  }

  const handleUpdateCategory = async (categoryId: string) => {
    try {
      await api.patch(`/api/categories/${categoryId}`, {
        name: categoryForm.name,
        icon: categoryForm.icon,
        color: categoryForm.color
      })
      setEditingCategoryId(null)
      await fetchData()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erreur')
    }
  }

  // Grouper les projets par catégorie
  const projectsByCategory = (catId: string) =>
    projects.filter(p => p.config.category_id === catId)

  const uncategorized = projects.filter(p => !p.config.category_id)

  // Catégories principales d'abord, puis les autres
  const MAIN_CATEGORIES = ['KERMHIT', 'VPS-HUB', 'FMA']
  const mainCats = categories.filter(c => MAIN_CATEGORIES.includes(c.id))
  const otherCats = categories.filter(c => !MAIN_CATEGORIES.includes(c.id))

  // Dropdown de déplacement
  const moveOptions = [...mainCats, ...otherCats.filter(c => !mainCats.find(m => m.id === c.id))]

  if (loading) return (
    <div style={{ backgroundColor: '#111827', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <Loader size={28} color="#ec4899" style={{ animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <span style={{ color: '#9ca3af' }}>Chargement...</span>
    </div>
  )

  if (error) return (
    <div style={{ backgroundColor: '#111827', height: '100%', padding: 24 }}>
      <div style={{ background: '#7f1d1d', border: '1px solid #991b1b', borderRadius: 8, padding: 16, color: '#fca5a5' }}>
        {error}
      </div>
    </div>
  )

  const renderProjectCard = (project: Project) => (
    <div
      key={project.name}
      onClick={() => onOpenProject(project.name)}
      style={{
        background: '#1f2937',
        border: '1px solid #374151',
        borderRadius: 8,
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        cursor: 'pointer',
        transition: 'border-color 0.2s'
      }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = '#6b7280')}
      onMouseLeave={e => (e.currentTarget.style.borderColor = '#374151')}
    >
      <Folder size={22} style={{ color: '#ec4899', flexShrink: 0 }} />
      <div style={{ overflow: 'hidden', minWidth: 0 }}>
        <div style={{ color: '#fff', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {project.config.name || project.name}
        </div>
        {project.config.description && (
          <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {project.config.description}
          </div>
        )}
      </div>
    </div>
  )

  const renderCategory = (category: Category, alwaysShow = false) => {
    const catProjects = projectsByCategory(category.id)
    if (!alwaysShow && catProjects.length === 0) return null
    const isCollapsed = collapsed.has(category.id)

    return (
      <div key={category.id} style={{ marginBottom: 32 }}>
        {/* Header catégorie */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            paddingBottom: 10,
            marginBottom: 16,
            borderBottom: `3px solid ${category.color}`,
            cursor: 'pointer',
            userSelect: 'none'
          }}
          onClick={() => toggleCollapse(category.id)}
        >
          {isCollapsed
            ? <ChevronRight size={18} color="#6b7280" />
            : <ChevronDown size={18} color="#6b7280" />
          }
          <span style={{ fontSize: 24 }}>{category.icon}</span>
          {editingCategoryId === category.id ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
              <input
                value={categoryForm.icon}
                onChange={e => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                maxLength={2}
                style={{ width: 40, background: '#111827', border: '1px solid #374151', borderRadius: 6, padding: '4px 6px', color: '#fff', fontSize: 18, textAlign: 'center' }}
              />
              <input
                value={categoryForm.name}
                onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                style={{ background: '#111827', border: '1px solid #374151', borderRadius: 6, padding: '6px 10px', color: '#fff', fontSize: 16, fontWeight: 'bold' }}
              />
              <input type="color" value={categoryForm.color} onChange={e => setCategoryForm({ ...categoryForm, color: e.target.value })}
                style={{ width: 36, height: 30, background: '#111827', border: '1px solid #374151', borderRadius: 6, cursor: 'pointer' }}
              />
              <button onClick={() => handleUpdateCategory(category.id)}
                style={{ background: '#10b981', border: 'none', borderRadius: 6, padding: '6px 10px', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <Save size={13} /> Sauver
              </button>
              <button onClick={() => setEditingCategoryId(null)}
                style={{ background: '#374151', border: 'none', borderRadius: 6, padding: '6px 8px', color: '#9ca3af', cursor: 'pointer' }}>
                <X size={13} />
              </button>
            </div>
          ) : (
            <>
              <h2 style={{ color: category.color, fontSize: 20, fontWeight: 'bold', margin: 0, flex: 1 }}>
                {category.name}
              </h2>
              <span style={{ color: '#6b7280', fontSize: 14 }}>({catProjects.length})</span>
              <button
                onClick={e => {
                  e.stopPropagation()
                  setEditingCategoryId(category.id)
                  setCategoryForm({ id: category.id, name: category.name, icon: category.icon, color: category.color })
                }}
                style={{ background: 'transparent', border: '1px solid #374151', borderRadius: 4, padding: '3px 8px', color: '#6b7280', cursor: 'pointer', fontSize: 12 }}
              >
                ✏️
              </button>
            </>
          )}
        </div>

        {/* Projets */}
        {!isCollapsed && (
          catProjects.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {catProjects.map(renderProjectCard)}
            </div>
          ) : (
            <div style={{ color: '#374151', fontStyle: 'italic', fontSize: 13, paddingLeft: 8 }}>
              Aucun projet dans cette catégorie
            </div>
          )
        )}
      </div>
    )
  }

  return (
    <div style={{ backgroundColor: '#111827', height: '100%', overflowY: 'auto', padding: '28px 32px' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <h1 style={{ color: '#fff', fontSize: 26, fontWeight: 'bold', margin: '0 0 6px 0' }}>
              📁 Projets
            </h1>
            <p style={{ color: '#6b7280', margin: 0, fontSize: 14 }}>
              {projects.length} projets · {mainCats.length} catégories principales
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => setShowCategoryForm(!showCategoryForm)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
            >
              <Plus size={15} /> Catégorie
            </button>
            <button
              onClick={() => setShowProjectForm(!showProjectForm)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#ec4899', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
            >
              <Plus size={15} /> Projet
            </button>
          </div>
        </div>

        {/* Formulaire nouvelle catégorie */}
        {showCategoryForm && (
          <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, padding: 20, marginBottom: 28 }}>
            <h3 style={{ color: '#fff', marginBottom: 14, fontSize: 15, fontWeight: 600 }}>Nouvelle catégorie</h3>
            {categoryFormError && (
              <div style={{ background: '#7f1d1d', color: '#fca5a5', border: '1px solid #ef4444', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
                {categoryFormError}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 60px', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ color: '#9ca3af', fontSize: 12, display: 'block', marginBottom: 6 }}>Nom *</label>
                <input value={categoryForm.name} onChange={e => {
                  const name = e.target.value
                  setCategoryForm({ ...categoryForm, name, id: slugify(name) })
                  setCategoryFormError(null)
                }}
                  placeholder="ex: Kermhit & Projets" style={{ width: '100%', background: '#111827', border: '1px solid #374151', borderRadius: 6, padding: '7px 10px', color: '#fff', fontSize: 13, boxSizing: 'border-box' }} />
                {categoryForm.id && <span style={{ color: '#6b7280', fontSize: 11, marginTop: 3, display: 'block' }}>ID : {categoryForm.id}</span>}
              </div>
              <div>
                <label style={{ color: '#9ca3af', fontSize: 12, display: 'block', marginBottom: 6 }}>Icône</label>
                <input value={categoryForm.icon} onChange={e => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                  maxLength={4} style={{ width: '100%', background: '#111827', border: '1px solid #374151', borderRadius: 6, padding: '7px 10px', color: '#fff', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ color: '#9ca3af', fontSize: 12, display: 'block', marginBottom: 6 }}>Couleur</label>
                <input type="color" value={categoryForm.color} onChange={e => setCategoryForm({ ...categoryForm, color: e.target.value })}
                  style={{ width: '100%', height: 34, background: '#111827', border: '1px solid #374151', borderRadius: 6, cursor: 'pointer', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAddCategory}
                style={{ padding: '7px 14px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                Créer
              </button>
              <button onClick={() => { setShowCategoryForm(false); setCategoryForm({ id: '', name: '', icon: '📁', color: '#6366f1' }) }}
                style={{ padding: '7px 14px', background: '#374151', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                Annuler
              </button>
            </div>
          </div>
        )}

        {/* Formulaire nouveau projet */}
        {showProjectForm && (
          <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, padding: 20, marginBottom: 28 }}>
            <h3 style={{ color: '#fff', marginBottom: 14, fontSize: 15, fontWeight: 600 }}>Nouveau projet</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ color: '#9ca3af', fontSize: 12, display: 'block', marginBottom: 6 }}>Nom * (slug)</label>
                <input value={newProject.name} onChange={e => setNewProject({ ...newProject, name: e.target.value.toLowerCase() })}
                  placeholder="mon-projet" style={{ width: '100%', background: '#111827', border: '1px solid #374151', borderRadius: 6, padding: '7px 10px', color: '#fff', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ color: '#9ca3af', fontSize: 12, display: 'block', marginBottom: 6 }}>Description</label>
                <input value={newProject.description} onChange={e => setNewProject({ ...newProject, description: e.target.value })}
                  placeholder="Description courte" style={{ width: '100%', background: '#111827', border: '1px solid #374151', borderRadius: 6, padding: '7px 10px', color: '#fff', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ color: '#9ca3af', fontSize: 12, display: 'block', marginBottom: 6 }}>Catégorie</label>
                <select value={newProject.category_id} onChange={e => setNewProject({ ...newProject, category_id: e.target.value })}
                  style={{ width: '100%', background: '#111827', border: '1px solid #374151', borderRadius: 6, padding: '7px 10px', color: '#fff', fontSize: 13, boxSizing: 'border-box' }}>
                  <option value="">— Sans catégorie —</option>
                  {moveOptions.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleCreateProject} disabled={creating}
                style={{ padding: '7px 14px', background: creating ? '#6b7280' : '#ec4899', color: '#fff', border: 'none', borderRadius: 6, cursor: creating ? 'not-allowed' : 'pointer', fontSize: 13 }}>
                {creating ? 'Création...' : 'Créer'}
              </button>
              <button onClick={() => { setShowProjectForm(false); setNewProject({ name: '', description: '', category_id: 'VPS-HUB' }) }}
                style={{ padding: '7px 14px', background: '#374151', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                Annuler
              </button>
            </div>
          </div>
        )}

        {/* 3 catégories principales — toujours visibles */}
        {mainCats.map(cat => renderCategory(cat, true))}

        {/* Autres catégories */}
        {otherCats.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
              Autres catégories
            </div>
            {otherCats.map(cat => renderCategory(cat, true))}
          </div>
        )}

        {/* Non classés */}
        {uncategorized.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 10, marginBottom: 16, borderBottom: '3px solid #374151', cursor: 'pointer' }}
              onClick={() => toggleCollapse('__uncategorized')}
            >
              {collapsed.has('__uncategorized')
                ? <ChevronRight size={18} color="#6b7280" />
                : <ChevronDown size={18} color="#6b7280" />
              }
              <h2 style={{ color: '#6b7280', fontSize: 18, fontWeight: 'bold', margin: 0, flex: 1 }}>
                📂 Non classés
              </h2>
              <span style={{ color: '#6b7280', fontSize: 14 }}>({uncategorized.length})</span>
            </div>
            {!collapsed.has('__uncategorized') && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                {uncategorized.map(renderProjectCard)}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  )
}
